import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { translate } from './Translate';
import formatter from '../utils/formatter';
import LineChart from 'recharts/es6/chart/LineChart';
import Line from 'recharts/es6/cartesian/Line';
import XAxis from 'recharts/es6/cartesian/XAxis';
import YAxis from 'recharts/es6/cartesian/YAxis';
import CartesianGrid from 'recharts/es6/cartesian/CartesianGrid';
import ResponsiveContainer from 'recharts/es6/component/ResponsiveContainer';
import Tooltip from 'recharts/es6/component/Tooltip';
import Legend from 'recharts/es6/component/Legend';
import { computed, action } from 'mobx';
import { RebaseHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { IRebaseOptions } from '@kurtosys/ksys-app-template/dist/models/data';
import { DataStore, TranslationStore } from '../stores';
import { Series } from '../models/configuration/chart/Series';
import { ChartConfig } from '../models/chart';
import { DataSetValue } from '../models/components/ChartStore';
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import { Theme } from '../models/Theme';
const ResponsiveChartContainer = styled<any, any>(
    ResponsiveContainer,
    'responsiveChartContainer',
    'chartLineChart'
)`
    ${propStyles};
`;

ResponsiveChartContainer.defaultProps = {
    fontSize: 'xs',
    minHeight: '200px',
};
interface ChartLineChartProps {
    theme?: Theme;
    dataStore?: DataStore;
    translationStore?: TranslationStore;
    config?: ChartConfig['config'];
    data?: Array<DataSetValue>;
    xAxis?: XAxis;
    yAxis?: YAxis;
    series?: Array<Series>;
    getThemeColor?: (color: string) => void;
    getLabelName?: (label: any) => { value: string };
    getSeriesName?: (series: Series) => string;
    getBaseFormatOptions?: (
        formatOptions: any,
        v?: any
    ) => {
        dataStore: DataStore;
        locale: string;
        format: any;
        type: any;
        currency: string;
        options: any;
        value: any;
    };
    renderAccessibleTable?: (
        data: Array<object>,
        columns?: Array<{
            label: string;
            dataKey: string;
            format?: any;
        }>
    ) => JSX.Element;
    aspect?: number;
    queryContext?: IQueryContext;
}
@inject('dataStore')
@inject('translationStore')
@observer
class ChartLineChart extends Component<ChartLineChartProps> {
    rebaser: RebaseHelper;

    constructor(props) {
        super(props);
        this.rebaser = new RebaseHelper(this.rebaseConfig);
    }

    get config() {
        const { config } = this.props;
        if (config) {
            return config;
        }
    }

    get dataStore(): DataStore {
        const { dataStore } = this.props;
        if (dataStore) {
            return dataStore;
        }
    }

    get queryContext(): any {
        const { queryContext } = this.props;
        if (queryContext) {
            return queryContext;
        }
    }

    get rebaseConfig(): IRebaseOptions {
        if (this.dataStore && this.config) {
            const { rebase, rebaseQueries } = this.config;
            if (rebase || rebaseQueries) {
                const { query } = this.dataStore.stores;
                if (query) {
                    const executionOptions = query.getExecutionOptionsUsingContext(
                        this.queryContext
                    );
                    const rebaseConfig = query.mergeQueriesAndProps<
                        IRebaseOptions
                    >(
                        {
                            props: { ...rebase },
                            queries: { ...rebaseQueries },
                        },
                        executionOptions
                    );
                    return rebaseConfig;
                }
            }
        }
    }

    @computed
    get data() {
        const { data } = this.props;
        const rebaseConfig = this.rebaseConfig;
        if (rebaseConfig && rebaseConfig.enabled) {
            return this.rebaser.rebase(data);
        }

        // create shallow copy which can be passed to lineChart since lineChart does not support ObservableArray
        return data.slice();
    }

    @computed
    get yDomain() {
        const { yAxis, series } = this.props;
        const { getTicksAroundPoint, data } = this;
        let values = [];
        // Get values from each series data key for min and max
        series.forEach(element => {
            data.map(row => {
                const value = row[element.dataKey];
                if (value) {
                    values.push(value);
                }
            });
        });

        let min = Math.min(...values);
        let max = Math.max(...values);

        // readjust domain min and max based on ticks
        if (yAxis.basePoint) {
            values = getTicksAroundPoint(
                yAxis.basePoint,
                min,
                max,
                yAxis.tickCount
            );
            min = Math.min(...values);
            max = Math.max(...values);
        }

        return [Math.floor(min), Math.ceil(max)];
    }

    @action
    getTicksAroundPoint(
        point: number,
        min: number,
        max: number,
        tickCount?: number
    ) {
        const count = tickCount || 5;
        const intervals = [
            0.01,
            0.02,
            0.05,
            0.1,
            0.2,
            0.5,
            1,
            2,
            5,
            10,
            20,
            50,
            100,
            200,
            500,
            1000,
            2000,
            5000,
            10000,
            20000,
            50000,
            100000,
            200000,
            500000,
            1000000,
            2000000,
            5000000,
            10000000,
            20000000,
            50000000,
            100000000,
            200000000,
            500000000,
        ];
        const actualMax = Math.max(max, point);
        const actualMin = Math.min(min, point);
        const gap = (actualMax - actualMin) * 100;
        const bestInterval = intervals.find(
            interval => interval >= gap / count
        );
        const ticks: Array<number> = [];
        ticks.push(point);
        if (bestInterval) {
            let maxRunner = point;
            while (maxRunner < actualMax) {
                maxRunner += bestInterval / 100;
                ticks.push(maxRunner);
            }
            let minRunner = point;
            while (minRunner > actualMin) {
                minRunner -= bestInterval / 100;
                ticks.push(minRunner);
            }
        }

        return ticks.sort((a, b) => a - b);
    }

    getSeriesLabel(s) {
        const { getSeriesName, translationStore } = this.props;
        return s.name ? getSeriesName(s) : translate(translationStore, s.label);
    }

    render() {
        const {
            theme,
            dataStore,
            config,
            xAxis,
            yAxis,
            series,
            getThemeColor,
            getLabelName,
            getBaseFormatOptions,
            renderAccessibleTable,
            aspect,
        } = this.props;

        const data = this.data;
        const { yDomain, getTicksAroundPoint } = this;

        if (yAxis.basePoint) {
            // yAxis props are spread on yAxis component. Add ticks only if config for basePoint is available
            yAxis.ticks = getTicksAroundPoint(
                yAxis.basePoint,
                yDomain[0],
                yDomain[1],
                yAxis.tickCount
            );
        }

        return (
            <React.Fragment>
                {renderAccessibleTable &&
                    renderAccessibleTable(data, [
                        {
                            label: getLabelName(xAxis.label).value,
                            dataKey: xAxis.dataKey,
                            format: config.tooltip.labelFormat
                                ? getBaseFormatOptions(
                                      config.tooltip.labelFormat
                                  )
                                : null,
                        },
                        ...series.map(s => {
                            return {
                                label: this.getSeriesLabel(s),
                                dataKey: s.dataKey,
                                format: config.tooltip.valueFormat
                                    ? getBaseFormatOptions(
                                          config.tooltip.valueFormat
                                      )
                                    : null,
                            };
                        }),
                    ])}
                <div aria-hidden="true">
                    <ResponsiveChartContainer
                        aspect={aspect}
                        {...theme.ResponsiveChartContainer}
                    >
                        <LineChart data={data}>
                            {config.grid && (
                                <CartesianGrid
                                    {...config.grid}
                                    stroke={getThemeColor(config.grid.stroke)}
                                    fill={getThemeColor(config.grid.fill)}
                                />
                            )}
                            {config.tooltip && (
                                <Tooltip
                                    cursor={{
                                        strokeWidth: 0,
                                        fill: 'rgba(0,0,0,0.05)',
                                    }}
                                    {...config.tooltip}
                                    labelFormatter={v =>
                                        config.tooltip.labelFormat
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      config.tooltip
                                                          .labelFormat,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                    formatter={v =>
                                        config.tooltip.valueFormat
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      config.tooltip
                                                          .valueFormat,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                />
                            )}
                            {config.legend && (
                                <Legend iconType="circle" {...config.legend} />
                            )}
                            {xAxis && (
                                <XAxis
                                    dataKey={xAxis.dataKey}
                                    tickLine={false}
                                    {...xAxis}
                                    label={getLabelName(xAxis.label)}
                                    tickFormatter={v =>
                                        xAxis.format
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      xAxis.format,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                />
                            )}
                            {yAxis && (
                                <YAxis
                                    dataKey={yAxis.dataKey}
                                    yAxisId="left"
                                    orientation="left"
                                    axisLine={false}
                                    tickLine={false}
                                    domain={yDomain}
                                    {...yAxis}
                                    label={getLabelName(yAxis.label)}
                                    tickFormatter={v =>
                                        yAxis.format
                                            ? formatter(
                                                  getBaseFormatOptions(
                                                      yAxis.format,
                                                      v
                                                  )
                                              )
                                            : v
                                    }
                                />
                            )}
                            {series.map((s, index) => (
                                <Line
                                    key={`line-${s.dataKey}-${index}`}
                                    dataKey={s.dataKey}
                                    yAxisId="left"
                                    dot={false}
                                    type="linear"
                                    {...s}
                                    name={this.getSeriesLabel(s)}
                                    stroke={getThemeColor(s.color)}
                                    isAnimationActive={!dataStore.isExpandAll}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveChartContainer>
                </div>
            </React.Fragment>
        );
    }
}

export default withTheme(ChartLineChart) as any;



// WEBPACK FOOTER //
// ./src/Components/ChartLineChart.tsx